import { useCallback, useEffect, useState } from 'react';
import { supabase, SUPABASE_URL } from '@/src/lib/supabase';
import type { AppRole } from '@/src/lib/types';
import type { DrawerAccessMap } from '@/src/lib/permissions';

export interface TeamMember {
  id: string;
  name: string;
  email: string | null;
  app_role: AppRole;
  area: string | null;
  active: boolean;
  auth_user_id?: string | null; // null = no login (e.g. added from Deputy, not invited yet)
  removed_at?: string | null;   // set = removed for good (hidden)
  venue_id?: string | null;     // the venue they currently have open (may be another of their venues)
  position?: string | null;     // job title (Head Chef, Waiter...), separate from access level
  deputy_hint?: string | null;  // area they're mostly rostered in (Deputy), shown as a suggestion only
}

// Calls the manager-only manage-access edge function.
async function call(action: string, payload: Record<string, unknown>): Promise<{ error: string | null }> {
  const { data: session } = await supabase.auth.getSession();
  if (!session.session) return { error: 'not signed in' };
  const resp = await fetch(`${SUPABASE_URL}/functions/v1/manage-access`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.session.access_token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...payload }),
  });
  const body = (await resp.json().catch(() => ({}))) as { ok?: boolean; error?: string };
  if (!resp.ok || !body.ok) return { error: body.error ?? `http ${resp.status}` };
  return { error: null };
}

function readDrawerAccess(settings: unknown): DrawerAccessMap {
  const da = (settings as { drawer_access?: DrawerAccessMap } | null)?.drawer_access;
  return da ?? {};
}

// Lightweight read of the visibility map — used by the shell to filter the drawer rail.
export function useDrawerAccess(orgId: string | null) {
  const [access, setAccess] = useState<DrawerAccessMap>({});

  const load = useCallback(async () => {
    if (!orgId) { setAccess({}); return; }
    const { data } = await supabase.from('organizations').select('settings').eq('id', orgId).maybeSingle();
    setAccess(readDrawerAccess(data?.settings));
  }, [orgId]);

  useEffect(() => { void load(); }, [load]);
  return { access, reload: load };
}

// Full team + visibility read with mutations — used by the Team & Access panel.
export function useTeamAccess(orgId: string | null, venueId: string | null) {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [access, setAccess] = useState<DrawerAccessMap>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!venueId) { setMembers([]); setLoading(false); return; }
    setLoading(true);
    const [m, o] = await Promise.all([
      // No venue filter: the database shows this venue's people, including people who work here
      // but currently have another of their venues open. Their role here comes from user_venue_access.
      supabase.from('users').select('*').order('name'),
      orgId
        ? supabase.from('organizations').select('settings').eq('id', orgId).maybeSingle()
        : Promise.resolve({ data: null as { settings?: unknown } | null }),
    ]);
    if (m.error) setError(m.error.message);
    // '*' keeps this working whether or not newer columns exist; removed people are hidden.
    const { data: grants } = await supabase.from('user_venue_access').select('user_id, role, is_active').eq('venue_id', venueId);
    const grantBy = new Map(((grants ?? []) as Array<{ user_id: string; role: string; is_active: boolean }>).map((g) => [g.user_id, g]));
    let rows = ((m.data as TeamMember[] | null) ?? [])
      .filter((u) => u.venue_id === venueId || grantBy.get(u.id)?.is_active)
      .map((u) => (u.venue_id === venueId ? u : { ...u, app_role: (grantBy.get(u.id)?.role ?? u.app_role) as TeamMember['app_role'], active: true }))
      .filter((u) => !(u.removed_at && !u.active));
    // Where people are mostly rostered (Deputy area), as a hint for their position.
    const since = new Date(Date.now() - 60 * 86_400_000).toISOString();
    const { data: sh } = await supabase.from('shifts').select('user_id, role_tag').eq('venue_id', venueId).gte('start_time', since).not('role_tag', 'is', null).limit(5000);
    const counts = new Map<string, Map<string, number>>();
    for (const r of (sh ?? []) as Array<{ user_id: string | null; role_tag: string | null }>) {
      if (!r.user_id || !r.role_tag) continue;
      const m2 = counts.get(r.user_id) ?? new Map<string, number>();
      m2.set(r.role_tag, (m2.get(r.role_tag) ?? 0) + 1);
      counts.set(r.user_id, m2);
    }
    rows = rows.map((u) => {
      const m2 = counts.get(u.id);
      const top = m2 ? [...m2.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] : null;
      return { ...u, deputy_hint: top ?? null };
    });
    setMembers(rows);
    setAccess(readDrawerAccess((o as { data: { settings?: unknown } | null }).data?.settings));
    setLoading(false);
  }, [orgId, venueId]);

  useEffect(() => { void load(); }, [load]);

  const setMemberRole = useCallback(async (userId: string, role: AppRole) => {
    const r = await call('set_role', { user_id: userId, role });
    if (!r.error) await load();
    return r;
  }, [load]);

  const setMemberActive = useCallback(async (userId: string, active: boolean) => {
    const r = await call('set_active', { user_id: userId, active });
    if (!r.error) await load();
    return r;
  }, [load]);

  const setMemberPosition = useCallback(async (userId: string, position: string | null) => {
    const r = await call('set_position', { user_id: userId, position });
    if (!r.error) await load();
    return r;
  }, [load]);

  const removeMember = useCallback(async (userId: string) => {
    const r = await call('remove_member', { user_id: userId });
    if (!r.error) await load();
    return r;
  }, [load]);

  const setDrawerAccess = useCallback(async (role: AppRole, drawerIds: string[]) => {
    const r = await call('set_drawer_access', { role, drawer_ids: drawerIds });
    if (!r.error) await load();
    return r;
  }, [load]);

  return { members, access, loading, error, setMemberRole, setMemberActive, setMemberPosition, removeMember, setDrawerAccess, reload: load,
    // Not switched off. Use for assignee pickers.
    activeMembers: members.filter((m) => m.active),
    // Active and signed up to OpsAlo (can open the app, take quizzes, see tasks).
    onOpsAlo: members.filter((m) => m.active && !!m.auth_user_id) };
}
